import type { JSX, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuthShell } from './context';
import { LOGIN_PATH, returnStateFor } from './returnPath';

/**
 * Route guard.
 *
 * Wraps the shell in the route table, so every screen under `/` needs a live
 * session and the login route, which sits beside the shell, does not. It reads
 * the provider-agnostic `AuthShellContext`: in a `none` build the status is
 * always `signedIn` and this renders its children straight away, which is what
 * keeps a build without login behaving exactly as before.
 *
 * Signed out: `Navigate` to the login screen with the current location in the
 * router state, so the login screen can send the user back to the screen they
 * asked for. `replace`, so Back does not return to a guarded screen that would
 * bounce again.
 */
export function RequireAuth({
  children,
  loading = null,
}: {
  readonly children: ReactNode;
  /** Rendered while the session is being read. */
  readonly loading?: ReactNode;
}): JSX.Element {
  const { status } = useAuthShell();
  const location = useLocation();

  if (status === 'loading') return <>{loading}</>;
  if (status === 'signedOut') {
    return <Navigate to={LOGIN_PATH} replace state={returnStateFor(location)} />;
  }
  return <>{children}</>;
}
